/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RecreateEEDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { HandleApiActions, labCar } from "../../../../redux/actions";
import actionType from "../../../../redux/actionTypes";
import typeApprovalAPI from "../../../../redux/actions/typeApprovalAPI";
import ruleEngineActions from "../../../../redux/actions/ruleEngineAction";
import customConsole from "../../../../config/customConsole";
import { Box, DialogContent, Stack, Typography } from "@mui/material";
import CustomDialogBoxV2 from "../../common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../../common/textField/CustomFormTextFieldV2";
import CustomSelectV2 from "../../common/select/CustomSelectV2";
import MultiselectConnectorV2 from "../../common/connectors/MultiselectConnectorV2";
import CustomButtonV2 from "../../common/button/CustomButtonV2";
import UsersMultiselect from "../../../labcar/UsersMultiselect";
import PropTypes from "prop-types";

function RecreateEEDialog({
  title,
  getZoneEECountList,
  openEEComponentReplaceDialog,
  setOpenEEComponentReplaceDialog,
  handleEEComponentReplace,
  selectedEEComponentReplaceDetails,
  setSelectedEEComponentReplaceDetails,
  vsOptionsList,
  setIsLinkSoftwareModulesScreen,
  selectedZone,
  selectedEEComponentDetails,
  getEEComponentsList,
  getSWModulesList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  variantOptionList,
  supplierOptionsList,
  recreationParentEEList,
  selectedParentEEDetails,
  setSelectedParentEEDetails,
  onClickAddForRecreate,
  getParentEEDetails,
  setRecreationParentEEList,
}) {
  const navigate = useNavigate();
  /* Replace EE Components Fields */
  const [assemblyNo, setAssemblyNo] = useState("");

  const [assemblyNoError, setAssemblyNoError] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [supplierNameError, setSupplierNameError] = useState("");
  const [version, setVersion] = useState("");
  const [versionError, setVersionError] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [zonePosition, setZonePosition] = useState(null);
  const [summary, setSummary] = useState("");
  const [summaryError, setSummaryError] = useState("");
  const [selectedVSIds, setSelectedVSIds] = useState([]);
  const [selectedVariantsMany, setSelectedVariantsMany] = useState([]);
  const [projectZoneList, setProjectZoneList] = useState([]);

  const [selectedASIL, setSelectedASIL] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [discp, setDiscp] = useState("");
  const [discpError, setDiscpError] = useState("");
  const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);
  const [ticketsDialogOpen, setTicketsDialogOpen] = useState(false);

  const [showIntersectionImpactPrompt, setShowIntersectionImpactPrompt] =
    useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);

  //Rule set
  const [showRuleSetListDialog, setShowRuleSetListDialog] = useState(false);
  const [ruleSetList, setRuleSetList] = useState([]);
  const [selectedRuleSet, setSelectedRuleSet] = useState("");
  // Rule set question flow
  const [ruleSetTree, setRuleSetTree] = useState(null);
  // path: [{ node: {type,data}, answer: 'yes'|'no'|null }]
  const [rulePath, setRulePath] = useState([]);

  // states for recreation
  const [coOwners, setCoOwners] = useState([]);
  const [editors, setEditors] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleAnswerAt = (idx, answer) => {
    if (!rulePath || idx < 0 || idx >= rulePath?.length) return;

    // copy up to index (inclusive) and set the chosen answer
    const newPath = rulePath
      ?.slice(0, idx + 1)
      ?.map((entry, i) => (i === idx ? { ...entry, answer } : entry));

    const currentEntry = newPath[idx];
    const nextBranch = currentEntry?.node?.data?.[answer + "_branch"];

    if (nextBranch) {
      // push the next node and reset its answer
      newPath.push({ node: nextBranch, answer: null });
      setRulePath(newPath);
    } else {
      // If we have a category, this is a terminal node
      if (currentEntry?.node?.data?.category) {
        // Check if homologation is required for this category
        if (currentEntry?.node?.data?.requires_homologation) {
          // If homologation is required, call updateChangesAndClose
          updateChangesAndClose();
          setRulePath(newPath);
          setShowRuleSetQuestionDialog(false);
          setRuleSetTree(null);
          setRulePath([]);
          setSelectedRuleSet("");
        } else {
          // If homologation is not required, just close the dialog
          updateChangesAndClose();
          setRulePath(newPath);
          setShowRuleSetQuestionDialog(false);
          setRuleSetTree(null);
          setRulePath([]);
          setSelectedRuleSet("");
        }
      } else {
        // Otherwise just update the current answer and keep dialog open
        setRulePath(newPath);
      }
    }
  };
  const [showRuleSetQuestionDialog, setShowRuleSetQuestionDialog] =
    useState(false);
  const dispatch = useDispatch();
  useEffect(() => {
    if (
      openEEComponentReplaceDialog == true &&
      selectedEEComponentReplaceDetails
    ) {
      setAssemblyNo(selectedEEComponentReplaceDetails?.assembly_number || "");
      setSupplierName(selectedEEComponentReplaceDetails?.supplier_name || "");
      setVersion(selectedEEComponentReplaceDetails?.version || "");
      setDescription(selectedEEComponentReplaceDetails?.description || "");
      setSummary(selectedEEComponentReplaceDetails?.summary || "");
      setSelectedVSIds(
        (selectedEEComponentReplaceDetails?.vehicle_systems || [])
          ?.map((vsId) => Number(vsId))
          ?.filter((vsId) => !isNaN(vsId)),
      );
      setSelectedASIL(
        selectedEEComponentReplaceDetails?.other_fields?.asil_class || null,
      );
      setZonePosition((prev) => {
        if (selectedProjectDetails.is_zonal) {
          return selectedEEComponentReplaceDetails?.zone_details?.position;
        } else {
          return null;
        }
      });
      setDiscp(selectedEEComponentReplaceDetails?.reason || "");
      setSelectedVariantsMany(
        variantOptionList
          ?.filter(
            (variant) =>
              variant?.value !== selectedSingleVariantDetails?.variant_id &&
              selectedEEComponentReplaceDetails?.variant_ids
                ?.map((variant) => variant)
                ?.includes(variant?.value),
          )
          ?.map((data) => data?.value) || [],
      );
    }
  }, [openEEComponentReplaceDialog, selectedEEComponentReplaceDetails]);

  /* -------------- Replace EE Component -------------- */

  const clearReplaceEEComponentFields = () => {
    setAssemblyNo("");
    setAssemblyNoError("");
    setSupplierName("");
    setSupplierNameError("");
    setVersion("");
    setVersionError("");
    setDescription("");
    setDescriptionError("");
    setSummary("");
    setSelectedVSIds([]);
    setSelectedASIL(null);
    setDiscp("");
    setDiscpError("");
    setZonePosition(null);
    setSelectedVariantsMany([]);
  };

  const isReplaceEEComponentValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;

    return (
      !assemblyNoError &&
      !supplierNameError &&
      !versionError &&
      !descriptionError &&
      selectedVSIds?.length > 0 &&
      selectedASIL?.length > 0 &&
      discp?.trim()?.length > 0 &&
      regexMin3?.test(assemblyNo?.trim()) &&
      regexMin3?.test(version?.trim()) &&
      assemblyNo?.trim()?.length >= 3 &&
      version?.trim()?.length >= 3 &&
      discp?.trim()?.length >= 3 &&
      (summary?.trim()?.length === 0 || !summaryError) &&
      (summary?.trim()?.length === 0 || summary?.trim()?.length >= 3) &&
      (assemblyNo?.trim() !==
        selectedEEComponentReplaceDetails?.assembly_number ||
        supplierName?.trim() !==
        selectedEEComponentReplaceDetails?.supplier_name ||
        // version?.trim() !== selectedEEComponentReplaceDetails?.version ||
        description?.trim() !==
        selectedEEComponentReplaceDetails?.description ||
        summary?.trim() !== selectedEEComponentReplaceDetails?.summary ||
        selectedVSIds?.length !==
        selectedEEComponentReplaceDetails?.vehicle_systems?.length ||
        selectedASIL !==
        selectedEEComponentReplaceDetails?.other_fields?.asil_class)
    );
  };

  const isUpdateDraftEEComponentValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;
    return (
      assemblyNo?.trim() !== "" &&
      assemblyNo?.trim()?.length >= 3 &&
      !assemblyNoError &&
      regexMin3?.test(assemblyNo?.trim()) &&
      supplierName?.length > 0 &&
      version?.trim() !== "" &&
      version?.trim()?.length >= 3 &&
      !versionError &&
      regexMin3?.test(version?.trim()) &&
      description?.trim() !== "" &&
      description?.trim()?.length >= 3 &&
      !descriptionError &&
      selectedVSIds?.length > 0 &&
      selectedASIL?.length > 0 &&
      !isLoading &&
      (!selectedProjectDetails?.is_zonal || zonePosition != null) &&
      (summary?.trim()?.length === 0 || !summaryError) &&
      (summary?.trim()?.length === 0 || summary?.trim()?.length >= 3)
    );
  };
  const isUpdateAgainDraftEEComponentValid = () => {
    return !assemblyNoError;
  };

  const handleCloseEEComponentReplace = () => {
    setOpenEEComponentReplaceDialog(false);
    clearReplaceEEComponentFields();
  };

  const handleRecreateClick = async () => {
    const isZonalProject = selectedProjectDetails.is_zonal;

    const reqObj = {
      current_time: new Date().toISOString(),
      variant_ids: selectedVariantsMany?.map(Number),
      project_id: selectedProjectDetails?.project_id,
      variant_id: selectedSingleVariantDetails?.variant_id,
      assembly_number: assemblyNo?.trim(),
      ...(supplierName && { supplier_name: supplierName }),
      version: version?.trim(),
      ...(summary && { summary }),
      description: description,
      vehicle_systems: selectedVSIds?.map(Number) || [],
      other_fields: {
        asil_class: selectedASIL,
      },
      save_as_draft: false,
      ...(isZonalProject && {
        zone_position: zonePosition,
      }),
      co_owners: coOwners?.map((data) => data?.value),
      edit_users: editors?.map((data) => data?.value),
      view_users: viewers?.map((data) => data?.value),
      parent_ee_id: selectedEEComponentReplaceDetails?.ee_id,
      parent_project_id: selectedProjectDetails?.parent_project_id,
    };
    try {
      setIsLoading(true);
      const response = await dispatch(
        HandleApiActions({
          ...labCar.recreate_ee,
          params: reqObj,
          show_toast: true,
        }),
      );
      if (!response.error) {
        setOpenEEComponentReplaceDialog(false);

        clearReplaceEEComponentFields();
        getEEComponentsList();
      }
      setIsLoading(false);
    } catch (error) {
    }
  };

  const handleUpdateDraftClickEE = () => {
    const reqObj = {
      project_id: selectedProjectDetails?.project_id,
      variant_id: selectedSingleVariantDetails?.variant_id,
      ee_id: selectedEEComponentDetails?.ee_id,
      ...(selectedProjectDetails?.is_zonal && {
        zone_position: selectedProjectDetails?.project_zones?.find(
          (zones) => zones?.zone_name === selectedZone?.zone_position_name,
        )?.position,
      }),
      ...(selectedVariantsMany?.length > 0
        ? { variant_ids: selectedVariantsMany }
        : []),
      save_as_draft: false,
      assembly_number: assemblyNo,
      supplier_name: supplierName,

      version: version?.trim(),
      ...(summary ? { summary: summary } : null),
      description: description,
      vehicle_systems: selectedVSIds?.map(Number),

      other_fields: {
        asil_class: selectedASIL,
      },

      current_time: moment().format(),
    };
    dispatch(
      HandleApiActions({
        api_name: "/VLC/ee_components/update/draft",
        https_method: "POST",
        action_type: actionType.ADD_EE_COMPONENT_DRAFT,
        description: "Add in Save As Draft EE Component",
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      getEEComponentsList();
      handleCloseEEComponentReplace();
      // getZoneEECountList(
      //   selectedProjectDetails?.project_id,
      //   selectedSingleVariantDetails?.variant_id
      // );
    });
  };

  const handleUpdateAgainDraftClickEE = () => {
    const reqObj = {
      project_id: selectedProjectDetails?.project_id,
      variant_id: selectedSingleVariantDetails?.variant_id,
      ee_id: selectedEEComponentDetails?.ee_id,
      ...(selectedProjectDetails?.is_zonal && {
        zone_position: selectedProjectDetails?.project_zones?.find(
          (zones) => zones?.zone_name === selectedZone?.zone_position_name,
        )?.position,
      }),
      variant_ids: selectedVariantsMany,
      save_as_draft: true,
      assembly_number: assemblyNo,
      ...(supplierName ? { supplier_name: supplierName } : null),

      ...(version ? { version: version?.trim() } : null),
      ...(summary ? { summary: summary } : null),
      ...(description ? { description: description } : null),
      ...(selectedVSIds?.length > 0
        ? { vehicle_systems: selectedVSIds?.map(Number) || [] }
        : null),
      ...(selectedASIL?.length > 0
        ? {
          other_fields: {
            asil_class: selectedASIL,
          },
        }
        : null),
      current_time: moment().format(),
    };

    dispatch(
      HandleApiActions({
        api_name: "/VLC/ee_components/update/draft",
        https_method: "POST",
        action_type: actionType.ADD_EE_COMPONENT_DRAFT,
        description: "Add in Save As Draft EE Component",
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      getEEComponentsList();
      handleCloseEEComponentReplace();
      // getZoneEECountList(
      //   selectedProjectDetails?.project_id,
      //   selectedSingleVariantDetails?.variant_id
      // );
    });
  };

  const confirmUpdateAndRedirect = async ({ willImptIntrOnly = false }) => {
    if (
      selectedEEComponentReplaceDetails &&
      selectedEEComponentReplaceDetails?.ee_id
    ) {
      const reqObj = {
        current_time: new Date().toISOString(),
        variant_ids: selectedVariantsMany?.map(Number),
        project_id: Number(selectedEEComponentReplaceDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
        assembly_number: assemblyNo?.trim(),
        ...(supplierName && { supplier_name: supplierName }),
        version: version?.trim(),
        ...(summary && { summary }),
        description: description,
        vehicle_systems: selectedVSIds?.map(Number) || [],
        other_fields: {
          asil_class: selectedASIL,
        },
        will_it_impact: true,
        reason: discp,
      };
      try {
        const response = await dispatch(
          HandleApiActions({
            ...labCar.replace_ee_component,
            params: reqObj,
            show_toast: true,
          }),
        );
        if (!response.error) {
          handleCloseEEComponentReplace();
          willImptIntrOnly
            ? setActionType2DialogOpen(true)
            : setActionType1DialogOpen(true);
          if (willImptIntrOnly) {
            const resp = await dispatch(
              HandleApiActions({
                ...labCar.update_action_type,
                params: {
                  current_time: new Date().toISOString(),
                  project_id: Number(
                    selectedEEComponentReplaceDetails?.project_id,
                  ),
                  variant_id: Number(selectedSingleVariantDetails?.variant_id),
                  ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
                  is_dependency_linking: true,
                },
                show_toast: false,
              }),
            );
          }
          getEEComponentsList();
          getSWModulesList();
        }
      } catch (error) {
      }
    }
  };

  const handleCloseEEComponentReplaceDialog = () => {
    setOpenEEComponentReplaceDialog(false);
  };

  const checkForStandardWithRuleSet = async () => {
    const reqObj = {
      project_id: Number(selectedProjectDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentDetails?.ee_id),
      tickets_type: 2,
    };

    try {
      // First API call
      const response = await dispatch(
        HandleApiActions({
          ...typeApprovalAPI.get_tickets,
          params: reqObj,
          show_toast: false,
        }),
      );
      handleCloseEEComponentReplaceDialog();
      setShowIntersectionImpactPrompt(false);

      // Extract and deduplicate standards
      const standardsWithRuleSet = [
        ...new Set(
          Array.isArray(response?.data)
            ? response.data
              ?.map((ticket) => ticket?.ls_details?.standard_number)
              ?.filter((std) => Boolean(std)) // remove empty/undefined
            : [],
        ),
      ];

      if (standardsWithRuleSet?.length > 0) {
        // Second API call
        const standardWithRuleSet = await dispatch(
          HandleApiActions({
            ...ruleEngineActions.get_rule_engine_with_standards,
            params: { standard: standardsWithRuleSet },
            show_toast: false,
          }),
        );

        if (
          standardWithRuleSet?.data &&
          (Array.isArray(standardWithRuleSet.data)
            ? standardWithRuleSet?.data?.length > 0
            : Object.keys(standardWithRuleSet?.data)?.length > 0)
        ) {
          setShowRuleSetListDialog(true);
          setRuleSetList(standardWithRuleSet.data);
        } else {
          updateChangesWithoutStandardAndClose();
        }

        return standardWithRuleSet.data || [];
      } else {
        updateChangesAndClose();
        return [];
      }
    } catch (error) {
      return false;
    }
  };

  const handleRuleSetListDialogClose = () => {
    setShowRuleSetListDialog(false);
    setSelectedRuleSet("");
    handleCloseEEComponentReplace();
  };

  const viewRuleSetQuestions = async (ruleSetIdParam) => {
    const ruleSetIdToUse = ruleSetIdParam || selectedRuleSet;
    try {
      const response = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.get_rule_set_tree,
          params: {
            rule_set_id: ruleSetIdToUse,
          },
          show_toast: false,
        }),
      );

      if (response?.data) {
        // Store the full tree and open the question dialog
        setRuleSetTree(response.data);
        // Normalize root node into { type, data }
        const root = response.data?.tree
          ? { type: "question", data: response.data.tree }
          : null;
        if (root) {
          setShowRuleSetQuestionDialog(true);
          setRulePath([{ node: root, answer: null }]);
        }
      }
    } catch (error) {
    }
  };

  const updateChangesWithoutStandardAndClose = async () => {
    const reqObj = {
      current_time: new Date().toISOString(),
      variant_ids: selectedVariantsMany,
      project_id: Number(selectedEEComponentReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
      assembly_number: assemblyNo?.trim(),
      ...(supplierName && { supplier_name: supplierName }),
      version: version?.trim(),
      ...(summary && { summary }),
      description: description,
      vehicle_systems: selectedVSIds?.map(Number) || [],
      other_fields: {
        asil_class: selectedASIL,
      },
      will_it_impact: true,
      reason: discp,
    };

    try {
      // Await the dispatch to ensure you're getting the response
      const response = await dispatch(
        HandleApiActions({
          ...labCar.replace_ee_component,
          params: reqObj,
          show_toast: true,
        }),
      );
      getEEComponentsList();
      getSWModulesList();
      if (!response.error) {
        handleCloseEEComponentReplace();
      }
      if (response?.data?.snow_resp) {
        setTicketsDialogOpen(true);
      }
    } catch (error) {
    }
  };

  const updateChangesAndClose = async () => {
    // Get requires_homologation value from the last node in the rule path
    const lastNode = rulePath[rulePath?.length - 1]?.node;
    const requiresHomologation = lastNode?.data?.requires_homologation || false;
    if (
      selectedEEComponentReplaceDetails &&
      selectedEEComponentReplaceDetails?.ee_id
    ) {
      const reqObj = {
        variant_ids: selectedVariantsMany?.map(Number),
        current_time: new Date().toISOString(),
        project_id: Number(selectedEEComponentReplaceDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
        assembly_number: assemblyNo?.trim(),
        supplier_name: supplierName,
        version: version?.trim(),
        ...(summary && { summary }),
        description: description,
        vehicle_systems: selectedVSIds?.map(Number) || [],
        other_fields: {
          asil_class: selectedASIL,
        },
        will_it_impact: false,
        requires_homologation: requiresHomologation,
        reason: discp,
      };
      try {
        const response = await dispatch(
          HandleApiActions({
            ...labCar.replace_ee_component,
            params: reqObj,
            show_toast: true,
          }),
        );
        getEEComponentsList();
        getSWModulesList();
        if (!response.error) {
          handleCloseEEComponentReplace();
        }
        if (response?.data?.snow_resp) {
          setTicketsDialogOpen(true);
        }
      } catch (error) {
      }
    }
  };

  const handleReplaceEEComponent = ({ }) => {
    if (
      selectedEEComponentReplaceDetails &&
      selectedEEComponentReplaceDetails?.ee_id
    ) {
      handleEEComponentReplace({
        ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
        assembly_number: assemblyNo?.trim(),
        supplier_name: supplierName,
        version: version?.trim(),
        summary: summary,
        description: description,
        vehicle_systems: selectedVSIds?.map(Number) || [],
        other_fields: {
          asil_class: selectedASIL,
        },
      });

      handleCloseEEComponentReplace();
    }
  };


  useEffect(() => {
    customConsole({ selectedVSIds, vsOptionsList });
  }, [selectedVSIds, vsOptionsList]);

  const handleActionType1DialogOpen = (ecu) => {
    setActionType1DialogOpen(true);
  };

  const handleActionType1DialogClose = () => {
    setActionType1DialogOpen(false);
  };

  /*************  ✨ Windsurf Command ⭐  *************/
  /**
   * Closes the action type 2 dialog.
   */
  /*******  8bbe441d-5db6-4801-843b-8073c92b7f62  *******/
  const handleActionType2DialogClose = () => {
    setActionType2DialogOpen(false);
  };

  const handleRedirectionToTypeApproval = () => {
    navigate(
      `/dashboard/v2/type-approval/link?ee_id=${selectedEEComponentDetails?.ee_id}&zone=${selectedZone?.zone_position_name}&tab=0`,
    );
    setActionType2DialogOpen(false);
  };

  const handleTicketsDialogClose = () => {
    setTicketsDialogOpen(false);
  };

  useEffect(() => {
    if (
      openEEComponentReplaceDialog &&
      selectedSingleVariantDetails?.variant_id != null
    ) {
      const id = selectedSingleVariantDetails.variant_id;
      const currentOptionValue =
        (variantOptionList || [])?.find((v) => String(v?.value) === String(id))
          ?.value ?? id;
      setSelectedVariantsMany((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const hasId = prevArr?.map(String).includes(String(currentOptionValue));
        if (hasId) return prevArr;
        return [currentOptionValue, ...prevArr];
      });
    }
  }, [
    openEEComponentReplaceDialog,
    selectedSingleVariantDetails,
    setSelectedVariantsMany,
  ]);

  return (
    <Box>
      <CustomDialogBoxV2
        open={openEEComponentReplaceDialog}
        onClose={handleCloseEEComponentReplace}
        title={title}
        successButtonName={title === "Replace EE Component" ? "Replace" : "Add"}
        rejectButtonName={
          title === "Replace EE Component" ? "Cancel" : "Cancel"
        }
        onDisabled={
          title === "Replace EE Component"
            ? !isReplaceEEComponentValid()
            : !isUpdateDraftEEComponentValid()
        }
        onClickSuccess={handleRecreateClick}
        // onClickSuccess={handleUpdateClick}
        onClickReject={
          title === "Replace EE Component"
            ? handleCloseEEComponentReplace
            : handleCloseEEComponentReplace
        }
        actionButtonStyle={"continuous"}
      >
        <Stack
          spacing={2}
          mb={2}
          justifyContent={"center"}
          sx={{ width: "450px" }}
        >
          <CustomSelectV2
            placeholder={"Select Parent EE Component"}
            label="Parent EE Component"
            required={true}
            selectedValue={selectedParentEEDetails}
            handleOnChange={(e) => {
              setSelectedParentEEDetails(e.target.value);
            }}
            optionsList={recreationParentEEList.map((item) => ({
              label: item?.assembly_number,
              value: item, // ✅ primitive
            }))}
          />
          <CustomFormTextFieldV2
            size={"small"}
            label={"Assembly No"}
            required={true}
            type={"text"}
            fldType={"textR"}
            value={assemblyNo || " "}
            setValue={(value) => value?.length <= 40 && setAssemblyNo(value)}
            maxLength={40}
          />{" "}
          <CustomFormTextFieldV2
            size={"small"}
            label={"Version"}
            required={true}
            type={"text"}
            fldType={"textR"}
            value={version}
            setValue={(value) => value?.length <= 40 && setVersion(value)}
            // valueError={versionError}
            // setValueError={setVersionError}
            maxLength={40}
          />
          <CustomFormTextFieldV2
            size={"small"}
            label={"Description"}
            required={true}
            type={"text"}
            fldType={"text"}
            value={description}
            setValue={(value) => value?.length <= 500 && setDescription(value)}
            valueError={descriptionError}
            setValueError={setDescriptionError}
            maxLength={500}
          />
          <CustomFormTextFieldV2
            size={"small"}
            label={"Summary"}
            type={"text"}
            fldType={"text"}
            value={summary}
            setValue={(value) => value?.length <= 250 && setSummary(value)}
            valueError={summaryError}
            setValueError={setSummaryError}
            maxLength={250}
          />
          {selectedProjectDetails?.is_zonal && (
            <CustomSelectV2
              placeholder={"Select Zone"}
              label="Zone"
              required={true}
              selectedValue={zonePosition}
              handleOnChange={(e) => {
                setZonePosition(e.target.value);
              }}
              optionsList={selectedProjectDetails?.project_zones?.map(
                (zone) => ({
                  value: zone.position,
                  label: zone.zone_name,
                }),
              )}
            />
          )}
          <CustomSelectV2
            size={"small"}
            required={true}
            label={"Select Supplier Name"}
            optionsList={supplierOptionsList}
            selectedValue={supplierName || " "}
            handleOnChange={(e) => setSupplierName(e.target.value)}
          />
          <Box>
            <MultiselectConnectorV2
              required
              multiple
              size={"small"}
              label={"Select Vehicle Systems"}
              optionsList={vsOptionsList?.map((el) => ({
                value: Number(el?.id),
                label: el?.name,
              }))}
              selectedValues={selectedVSIds || " "}
              setSelectedValues={setSelectedVSIds}
              placeholder={false}
            />
          </Box>
          <CustomSelectV2
            size={"small"}
            label={"Select ASIL Class"}
            required={true}
            optionsList={["A", "B", "C", "D"]?.map((el) => ({
              value: el,
              label: el,
            }))}
            selectedValue={selectedASIL || " "}
            handleOnChange={(e) => setSelectedASIL(e.target.value)}
          />
          <MultiselectConnectorV2
            multiple
            selectedValues={selectedVariantsMany}
            setSelectedValues={setSelectedVariantsMany}
            label={"Select Variants"}

            optionsList={variantOptionList}
            immutableValues={
              selectedSingleVariantDetails?.variant_id != null
                ? [selectedSingleVariantDetails?.variant_id]
                : []
            }
            placeholder={false}
          />
          <UsersMultiselect
            coOwnerDetails={
              selectedEEComponentReplaceDetails?.co_owner_details || []
            }
            editorDetails={
              selectedEEComponentReplaceDetails?.edit_user_details || []
            }
            viewerDetails={
              selectedEEComponentReplaceDetails?.view_user_details || []
            }
            coOwners={coOwners}
            setCoOwners={setCoOwners}
            editors={editors}
            setEditors={setEditors}
            viewers={viewers}
            setViewers={setViewers}
            owner={
              selectedEEComponentReplaceDetails?.owner_details?.[0].user_id
                ? [
                  selectedEEComponentReplaceDetails?.owner_details?.[0]
                    .user_id,
                ]
                : []
            }
          />
          {title === "Replace EE Component" ? (
            <CustomFormTextFieldV2
              size={"small"}
              label={"Reason"}
              required={true}
              type={"text"}
              fldType={"textO"}
              value={discp}
              setValue={(value) => value?.length <= 100 && setDiscp(value)}
              valueError={discpError}
              maxLength={100}

            />
          ) : null}
          <CustomDialogBoxV2
            open={showConfirmation}
            onClose={() => {
              setShowConfirmation(false);
            }}
            title="Action"
            dialogActions={
              <Box
                sx={{
                  display: "flex",
                  width: "350px",
                  justifyContent: "space-between",
                  height: "50px",
                }}
              >
                <CustomButtonV2
                  size="small"
                  type="cancel"
                  btnName="No"
                  handleOnClick={() => {
                    setShowConfirmation(false);

                    setTimeout(() => {
                      setShowIntersectionImpactPrompt(true);
                    }, 200);
                  }}
                />
                <CustomButtonV2
                  size="small"
                  type="submit"
                  btnName="Yes"
                  handleOnClick={confirmUpdateAndRedirect}
                />
              </Box>
            }
          >
            <Box
              sx={{
                width: "350px",
                height: "50px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  // ...componentStyle.font.text,
                  fontSize: "15px",
                }}
              >
                Will it impact the existing dependencies?
              </Typography>
            </Box>
          </CustomDialogBoxV2>
          <CustomDialogBoxV2
            open={showIntersectionImpactPrompt}
            onClose={() => {
              setShowIntersectionImpactPrompt(false);
            }}
            title="Action"
            dialogActions={
              <Box
                sx={{
                  display: "flex",
                  width: "350px",
                  justifyContent: "space-between",
                  height: "50px",
                }}
              >
                <CustomButtonV2
                  size="small"
                  type="cancel"
                  btnName="No"
                  handleOnClick={checkForStandardWithRuleSet}
                />
                <CustomButtonV2
                  size="small"
                  type="submit"
                  btnName="Yes"
                  handleOnClick={() =>
                    confirmUpdateAndRedirect({ willImptIntrOnly: true })
                  }
                />
              </Box>
            }
          >
            <Box
              sx={{
                width: "350px",
                height: "50px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  // ...componentStyle.font.text,
                  fontSize: "15px",
                }}
              >
                Will it impact the existing intersection?
              </Typography>
            </Box>
          </CustomDialogBoxV2>
        </Stack>
      </CustomDialogBoxV2>

      {/* Action Dialog Type 1 for EE Component Dependency Linking */}
      <Box>
        <CustomDialogBoxV2
          open={actionType1DialogOpen}
          onClose={handleActionType1DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            setIsLinkSoftwareModulesScreen(true);
            navigate(
              `/dashboard/v2/link?ee_id=${selectedEEComponentDetails?.ee_id}&selectedZone=${selectedZone?.zone_position_name}`,
            );
          }} // Trigger the update process
          onClickReject={handleActionType1DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography>EE Component dependency linking is pending!</Typography>
          </Stack>
        </CustomDialogBoxV2>

        <CustomDialogBoxV2
          open={actionType2DialogOpen}
          onClose={handleActionType2DialogClose}
          title={"Action Required"}
          onClickSuccess={handleRedirectionToTypeApproval} // Trigger the update process
          onClickReject={handleActionType2DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography>
              EE Component intersection linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>

        {/* Action Pending Dialog */}

        {/* Handle Ticket Page Dialog */}
        <CustomDialogBoxV2
          open={ticketsDialogOpen}
          onClose={handleTicketsDialogClose}
          title={"Tickets Recreated"}
          dialogActions={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <CustomButtonV2
                btnName="Close"
                handleOnClick={handleTicketsDialogClose}
                type="cancel"
              />
              <CustomButtonV2
                btnName="View"
                handleOnClick={() => {
                  navigate(`/dashboard/v2/type-approval/tickets`);
                  setTicketsDialogOpen(false);
                }}
              />
            </Box>
          }
        >
          <Box
            sx={{
              width: "350px",
              height: "50px",
              alignItems: "center",
              display: "flex",
            }}
          >
            <Typography
              sx={{
                // ...componentStyle.font.text,
                fontSize: "15px",
              }}
            >
              Successfully created the tickets for the intersections!
            </Typography>
          </Box>
        </CustomDialogBoxV2>

        {/* Rule set list dialog */}
        <CustomDialogBoxV2
          open={showRuleSetListDialog}
          onClose={handleRuleSetListDialogClose}
          title="Select Rule Set"
          onClickSuccess={async () => {
            // fetch and open the question flow for the selected ruleset
            await viewRuleSetQuestions(selectedRuleSet);
            // keep the list dialog closed
            setShowRuleSetListDialog(false);
          }}
          onDisabled={!selectedRuleSet}
          onClickReject={handleRuleSetListDialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Confirm"}
          rejectButtonName={"Cancel"}
        >
          <Stack spacing={2} sx={{ width: "350px", height: "50px" }}>
            <CustomSelectV2
              size={"small"}
              label={"Select Rule Set"}
              required={true}
              optionsList={
                ruleSetList?.map((rs) => ({
                  value: rs?.rule_set_id || rs?.id,
                  label: rs?.name,
                })) || []
              }
              selectedValue={selectedRuleSet || " "}
              handleOnChange={(e) => setSelectedRuleSet(e.target.value)}
            />
          </Stack>
        </CustomDialogBoxV2>

        {/* Rule set question dialog - progressive yes/no navigation */}
        <CustomDialogBoxV2
          open={showRuleSetQuestionDialog}
          onClose={() => {
            setShowRuleSetQuestionDialog(false);
            setRuleSetTree(null);
            setRulePath([]);
            setSelectedRuleSet("");
          }}
          title={`Rule Set: ${ruleSetTree?.name || ""}`}
          contentSx={{ minHeight: "350px", width: "450px" }}
        >
          <DialogContent>
            <Stack spacing={2} sx={{}}>
              {/* Full path view - show all questions answered so far and allow editing */}
              <Stack spacing={1}>
                {rulePath?.map((entry, idx) => {
                  const isCategory = entry.node?.type === "category";
                  return (
                    <Box
                      key={idx}
                      sx={{
                        border: "1px solid #e0e0e0",
                        p: 1,
                        borderRadius: 1,
                        backgroundColor:
                          idx === rulePath?.length - 1 ? "#fafafa" : "white",
                      }}
                    >
                      {!isCategory ? (
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {entry.node?.data?.text}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Box>
                              <CustomButtonV2
                                btnName={"Yes"}
                                handleOnClick={() => handleAnswerAt(idx, "yes")}
                                {...(entry.answer === "yes"
                                  ? { type: "submit" }
                                  : { type: "cancel" })}
                              />
                            </Box>
                            <Box>
                              <CustomButtonV2
                                btnName={"No"}
                                handleOnClick={() => handleAnswerAt(idx, "no")}
                                {...(entry.answer === "no"
                                  ? { type: "submit" }
                                  : { type: "cancel" })}
                              />
                            </Box>
                          </Box>
                        </Stack>
                      ) : (
                        <Stack>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Session Ended: {entry.node?.data?.description}
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                  );
                })}

                {/* If last node is category show Finish, otherwise allow answering last question if unanswered */}
                {rulePath?.length > 0 &&
                  rulePath[rulePath?.length - 1]?.node?.type === "category" ? (
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    mt={2}
                  >
                    <CustomButtonV2
                      type="submit"
                      btnName={"Finish"}
                      handleOnClick={() => {
                        // Check if the last node requires homologation
                        const lastNode = rulePath[rulePath?.length - 1]?.node;

                        if (lastNode?.data?.requires_homologation) {
                          // If homologation is required, just call the updateChangesAndClose function and close the dialog
                          updateChangesAndClose();
                          setShowRuleSetQuestionDialog(false);
                          setRuleSetTree(null);
                          setRulePath([]);
                          setSelectedRuleSet("");
                          handleCloseEEComponentReplace();
                        } else {
                          // If homologation is not required, just close the dialog
                          updateChangesAndClose();
                          setShowRuleSetQuestionDialog(false);
                          setRuleSetTree(null);
                          setRulePath([]);
                          setSelectedRuleSet("");
                          handleCloseEEComponentReplace();
                        }
                      }}
                    />
                  </Stack>
                ) : null}
              </Stack>
            </Stack>
          </DialogContent>
        </CustomDialogBoxV2>
      </Box>
    </Box>
  );
}

RecreateEEDialog.propTypes = {
  // Title
  title: PropTypes.string,

  // Dialog control
  openEEComponentReplaceDialog: PropTypes.bool,
  setOpenEEComponentReplaceDialog: PropTypes.func,

  // API / Data fetch
  getZoneEECountList: PropTypes.func,
  getEEComponentsList: PropTypes.func,
  getSWModulesList: PropTypes.func,
  getParentEEDetails: PropTypes.func,

  // Handlers
  handleEEComponentReplace: PropTypes.func,
  setSelectedEEComponentReplaceDetails: PropTypes.func,
  setIsLinkSoftwareModulesScreen: PropTypes.func,
  onClickAddForRecreate: PropTypes.func,
  setRecreationParentEEList: PropTypes.func,
  setSelectedParentEEDetails: PropTypes.func,

  // Lists
  vsOptionsList: PropTypes.array,
  variantOptionList: PropTypes.array,
  supplierOptionsList: PropTypes.array,
  recreationParentEEList: PropTypes.array,

  // Selected objects
  selectedEEComponentReplaceDetails: PropTypes.object,
  selectedZone: PropTypes.object,
  selectedEEComponentDetails: PropTypes.object,
  selectedProjectDetails: PropTypes.object,
  selectedSingleVariantDetails: PropTypes.object,
  selectedParentEEDetails: PropTypes.object,
};

export default RecreateEEDialog;
